import React, { useState, useRef, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  Button, 
  Typography, 
  Box, 
  Divider, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Chip,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tabs,
  Tab,
  TextField
} from '@mui/material';
import { 
  Print as PrintIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  FormatListBulleted as FormatListBulletedIcon,
  Assignment as AssignmentIcon,
  EventNote as EventNoteIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import JsBarcode from 'jsbarcode';

// Barcode Component
const Barcode = ({ value, width = 1.5, height = 40, fontSize = 12, displayValue = true }) => {
  const barcodeRef = useRef();
  
  useEffect(() => {
    if (barcodeRef.current && value) {
      try {
        JsBarcode(barcodeRef.current, value, {
          format: "CODE128",
          lineColor: "#000",
          width: width,
          height: height,
          displayValue: displayValue,
          fontSize: fontSize,
          margin: 5,
          background: "#ffffff"
        });
      } catch (error) {
        console.error("Error generating barcode:", error);
      }
    }
  }, [value, width, height, fontSize, displayValue]);
  
  return <svg ref={barcodeRef} className="barcode-element"></svg>;
};

// Add print styles to the document
const addPrintStyles = () => {
  const styleId = 'task-print-styles';
  
  // Remove the style if it already exists
  if (document.getElementById(styleId)) {
    document.getElementById(styleId).remove();
  }
  
  // Create a new style element
  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    @media print {
      html, body {
        -webkit-print-color-adjust: exact !important;
        color-adjust: exact !important;
        print-color-adjust: exact !important;
        height: auto !important;
        overflow: visible !important;
      }
      
      body * {
        visibility: hidden;
      }
      
      .printable-content, .printable-content * {
        visibility: visible !important;
        color: black !important;
      }
      
      .printable-content {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }
      
      .MuiChip-root {
        border: 1px solid #000000 !important;
        background-color: #ffffff !important;
        color: #000000 !important;
      }
      
      .MuiPaper-root {
        box-shadow: none !important;
        border: 1px solid #000000 !important;
        background-color: #ffffff !important;
      }
      
      .MuiTableCell-root {
        color: #000000 !important;
        border-color: #000000 !important;
        padding: 8px !important;
      }
      
      .MuiTableHead-root .MuiTableCell-root {
        background-color: #f5f5f5 !important;
        font-weight: bold !important;
      }
      
      .no-print {
        display: none !important;
      }
      
      .barcode-element {
        max-width: 100%;
        display: inline-block !important;
        background-color: #ffffff !important;
      }
      
      svg.barcode-element {
        height: auto !important;
        max-height: 50px !important;
      }
    }
  `;
  
  // Add the style to the document head
  document.head.appendChild(style);
};

// Styled components for print
const PrintableArea = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#ffffff',
  color: '#000000',
  // Make sure all content is visible in print mode
  '@media print': {
    backgroundColor: '#ffffff !important',
    color: '#000000 !important',
    width: '100%',
    height: 'auto',
    overflow: 'visible',
    margin: 0,
    padding: theme.spacing(2),
    '& *': {
      color: '#000000 !important',
      visibility: 'visible !important',
    },
    '& .MuiChip-root': {
      border: '1px solid #000000 !important',
      backgroundColor: '#ffffff !important',
      color: '#000000 !important',
    },
    '& .MuiPaper-root': {
      boxShadow: 'none !important',
      border: '1px solid #000000 !important',
      backgroundColor: '#ffffff !important',
    },
    '& .MuiTableCell-root': {
      color: '#000000 !important',
      borderColor: '#000000 !important',
      padding: '8px !important',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      backgroundColor: '#f5f5f5 !important',
      fontWeight: 'bold !important',
    },
    '& .task-description': {
      maxHeight: 'none !important',
    },
    '& .form-field-box': {
      border: '1px solid #000000 !important',
      backgroundColor: '#ffffff !important',
      height: '30px !important',
      minWidth: '100px !important',
      marginTop: '4px !important',
      marginBottom: '8px !important',
    },
    '& .form-field-textarea': {
      border: '1px solid #000000 !important',
      backgroundColor: '#ffffff !important',
      height: '70px !important',
      minWidth: '100% !important',
      marginTop: '4px !important',
      marginBottom: '8px !important',
    },
    '& .form-section': {
      marginBottom: '15px !important',
      pageBreakInside: 'avoid !important',
    },
    '& .signature-box': {
      border: '1px solid #000000 !important',
      height: '70px !important',
      width: '100% !important',
      marginTop: '4px !important',
    },
    '& .checkbox-container': {
      display: 'flex !important',
      alignItems: 'center !important',
      marginTop: '6px !important',
    },
    '& .checkbox': {
      width: '18px !important',
      height: '18px !important',
      border: '1.5px solid #000000 !important',
      marginRight: '8px !important',
      display: 'inline-block !important',
    },
    '& .logbook-header': {
      borderBottom: '2px solid #000000 !important',
      paddingBottom: '10px !important',
      marginBottom: '15px !important',
    },
    '& .table-container': {
      marginTop: '20px !important',
      marginBottom: '20px !important',
    }
  }
}));

const PrintHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  '@media print': {
    display: 'flex',
  }
}));

const PrintFooter = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(3),
  textAlign: 'center',
  fontSize: '0.8rem',
  color: '#666',
  '@media print': {
    color: '#000000',
  }
}));

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', { 
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// Calculate total time for a task
const calculateTotalTime = (task) => {
  // Check if we have a pre-calculated total time
  if (task.totalTime) {
    return formatTimeDisplay(task.totalTime);
  }
  
  // Otherwise calculate from timers
  let totalMilliseconds = 0;
  
  // Add current session time if timer is running
  if (task.startTime && (!task.endTime || new Date(task.startTime) > new Date(task.endTime))) {
    totalMilliseconds += (new Date() - new Date(task.startTime));
  } 
  // Add completed session time
  else if (task.startTime && task.endTime) {
    totalMilliseconds += (new Date(task.endTime) - new Date(task.startTime));
  }
  
  // Add previous session times
  if (task.previousTimers && Array.isArray(task.previousTimers)) {
    totalMilliseconds += task.previousTimers.reduce((total, session) => {
      if (session.startTime && session.endTime) {
        return total + (new Date(session.endTime) - new Date(session.startTime));
      }
      return total;
    }, 0);
  }
  
  return formatTimeDisplay(totalMilliseconds);
};

// Format milliseconds to HH:MM:SS
const formatTimeDisplay = (milliseconds) => {
  if (!milliseconds) return '00:00:00';
  
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Task Status translation
const getStatusTranslation = (status) => {
  switch (status) {
    case 'assigned': return 'Zugewiesen';
    case 'in-progress': return 'In Bearbeitung';
    case 'completed': return 'Abgeschlossen';
    default: return status;
  }
};

// Get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'assigned': return '#4361ee';
    case 'in-progress': return '#f39c12';
    case 'completed': return '#2ecc71';
    default: return '#718096';
  }
};

const TaskPrintModal = ({ open, onClose, tasks, allUsers, isDarkMode }) => {
  const taskListRef = useRef();
  const fillableFormRef = useRef();
  const taskOverviewRef = useRef();
  
  const [tabValue, setTabValue] = useState(0);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('all');
  const [includeCompleted, setIncludeCompleted] = useState(true);
  const [companyDetails, setCompanyDetails] = useState({
    name: 'Heidenau CNC',
    address: 'Heidenauer Straße 42, 01809 Heidenau',
    contact: 'Tel: +49 123 456789',
    email: 'info@heidenau-cnc.de'
  });
  
  // Add print styles when component mounts
  useEffect(() => {
    if (open) {
      addPrintStyles();
      console.log("Print styles added to document");
    }
    
    // Cleanup function to remove styles when component unmounts
    return () => {
      const styleElement = document.getElementById('task-print-styles');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [open]);
  
  // Form data for fillable form
  const [taskForm, setTaskForm] = useState({
    taskId: '',
    title: '',
    description: '',
    assignedTo: '',
    startDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    priority: 'Medium',
    status: 'In Bearbeitung',
    timeSpent: '',
    notes: '',
    signature: ''
  });
  
  // Filter tasks based on selections
  const filteredTasks = tasks.filter(task => {
    // Filter by user if selected
    if (selectedUser && selectedUser !== '') {
      // Check both direct user property and assignedTo object
      const assignedToId = task.assignedTo?._id;
      const directUserId = task.user;
      
      // If neither match the selected user, filter out this task
      if (assignedToId !== selectedUser && directUserId !== selectedUser) {
        return false;
      }
    }
    
    // Filter by completion status
    if (!includeCompleted && (task.status === 'completed' || task.column === 'done')) {
      return false;
    }
    
    // Filter by date range if not 'all'
    if (selectedDateRange !== 'all') {
      const today = new Date();
      const taskDate = task.deadline ? new Date(task.deadline) : null;
      
      if (!taskDate) return true; // Include tasks without deadline
      
      if (selectedDateRange === 'today') {
        return taskDate.toDateString() === today.toDateString();
      } else if (selectedDateRange === 'thisWeek') {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return taskDate >= startOfWeek && taskDate <= endOfWeek;
      } else if (selectedDateRange === 'thisMonth') {
        return taskDate.getMonth() === today.getMonth() && 
               taskDate.getFullYear() === today.getFullYear();
      }
    }
    
    return true;
  });
  
  // Find user name by id
  const getUserName = (userId) => {
    const user = allUsers.find(u => u._id === userId);
    return user ? user.displayName : 'Nicht zugewiesen';
  };
  
  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle task form changes
  const handleTaskFormChange = (e) => {
    const { name, value } = e.target;
    setTaskForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Pre-fill task form from an existing task for print
  const prefillTaskForm = (task) => {
    setTaskForm({
      taskId: task.aufgabeId || task.id.substring(0, 6),
      title: task.title || '',
      description: task.description || '',
      anr: task.anr || '',
      assignedTo: task.assignedTo ? getUserName(task.assignedTo._id) : getUserName(task.user) || '',
      deadline: task.deadline ? formatDate(task.deadline) : '',
      currentDate: currentDate
    });
    setTabValue(1); // Switch to print form tab
  };
  
  // Print configuration 
  const printConfig = {
    removeAfterPrint: true,
    copyStyles: true,
    suppressErrors: false, // Show errors for debugging
    documentTitle: 'Heidenau CNC - Aufgabenliste',
    onBeforeGetContent: () => {
      console.log("Before getting content");
      return Promise.resolve();
    },
    onBeforePrint: () => {
      console.log('Print started');
      return Promise.resolve();
    },
    onPrintError: (error) => {
      console.error('Print error:', error);
      toast.error('Druckfehler: Bitte versuchen Sie es erneut');
    },
    onAfterPrint: () => {
      console.log('Print completed');
      toast.success('Dokument erfolgreich gedruckt');
    }
  };
  
  // Create print handlers using the useReactToPrint hook
  const printTaskList = useReactToPrint({
    ...printConfig,
    content: () => taskListRef.current,
    documentTitle: `Aufgabenliste_${new Date().toISOString().split('T')[0]}`
  });
  
  const printFillableForm = useReactToPrint({
    ...printConfig,
    content: () => fillableFormRef.current,
    documentTitle: `Aufgabenformular_${new Date().toISOString().split('T')[0]}`
  });
  
  const printTaskOverview = useReactToPrint({
    ...printConfig,
    content: () => taskOverviewRef.current,
    documentTitle: `Aufgabenübersicht_${new Date().toISOString().split('T')[0]}`
  });
  
  // Print function for task list with verification
  const handlePrintTaskList = () => {
    if (!taskListRef || !taskListRef.current) {
      console.error("Task list reference is not available");
      toast.error("Druckbereich konnte nicht gefunden werden");
      return;
    }
    printTaskList();
  };
  
  // Print function for fillable form with verification
  const handlePrintFillableForm = () => {
    if (!fillableFormRef || !fillableFormRef.current) {
      console.error("Fillable form reference is not available");
      toast.error("Druckbereich konnte nicht gefunden werden");
      return;
    }
    printFillableForm();
  };
  
  // Print function for task overview with verification
  const handlePrintTaskOverview = () => {
    if (!taskOverviewRef || !taskOverviewRef.current) {
      console.error("Task overview reference is not available");
      toast.error("Druckbereich konnte nicht gefunden werden");
      return;
    }
    printTaskOverview();
  };
  
  // General print handler based on current tab
  const handlePrint = () => {
    try {
      if (tabValue === 0) {
        handlePrintTaskList();
      } else if (tabValue === 1) {
        handlePrintFillableForm();
      } else if (tabValue === 2) {
        handlePrintTaskOverview();
      }
    } catch (error) {
      console.error("Printing error:", error);
      toast.error("Fehler beim Drucken. Bitte versuchen Sie es erneut.");
    }
  };
  
  // Get current date/time for report header
  const currentDate = new Date().toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: isDarkMode ? '#1a202c' : '#ffffff',
          color: isDarkMode ? '#f7fafc' : '#2d3748',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          Aufgabenprotokoll und Formulare
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          variant="fullWidth"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<FormatListBulletedIcon />} 
            label="Aufgabenliste" 
            id="tab-0" 
            aria-controls="tabpanel-0"
          />
          <Tab 
            icon={<DescriptionIcon />} 
            label="Ausfüllbares Formular" 
            id="tab-1" 
            aria-controls="tabpanel-1"
          />
          <Tab 
            icon={<AssignmentIcon />} 
            label="Task Übersicht" 
            id="tab-2" 
            aria-controls="tabpanel-2"
          />
        </Tabs>
        
        {/* Tab 1: Task List */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            {/* Filter options */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fc',
                borderRadius: 2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2
              }}>
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel id="user-filter-label">Mitarbeiter</InputLabel>
                  <Select
                    labelId="user-filter-label"
                    value={selectedUser}
                    label="Mitarbeiter"
                    onChange={(e) => setSelectedUser(e.target.value)}
                  >
                    <MenuItem value="">Alle Mitarbeiter</MenuItem>
                    {allUsers.map((user) => (
                      <MenuItem key={user._id} value={user._id}>
                        {user.displayName} ({user.userModel})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl sx={{ minWidth: 200 }} size="small">
                  <InputLabel id="date-filter-label">Zeitraum</InputLabel>
                  <Select
                    labelId="date-filter-label"
                    value={selectedDateRange}
                    label="Zeitraum"
                    onChange={(e) => setSelectedDateRange(e.target.value)}
                  >
                    <MenuItem value="all">Alle Zeiträume</MenuItem>
                    <MenuItem value="today">Heute</MenuItem>
                    <MenuItem value="thisWeek">Diese Woche</MenuItem>
                    <MenuItem value="thisMonth">Dieser Monat</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={includeCompleted} 
                      onChange={(e) => setIncludeCompleted(e.target.checked)}
                    />
                  }
                  label="Abgeschlossene Aufgaben einbeziehen"
                />
              </Box>
            </Grid>
            
            {/* Print actions */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<FilterIcon />}
                >
                  Filter anwenden
                </Button>
                <Box>
                  {filteredTasks.length > 0 && (
                    <Button 
                      variant="outlined" 
                      color="primary"
                      sx={{ mr: 2 }}
                      onClick={() => prefillTaskForm(filteredTasks[0])}
                    >
                      Als Formular verwenden
                    </Button>
                  )}
                  <Button 
                    variant="contained" 
                    startIcon={<PrintIcon />}
                    onClick={handlePrintTaskList}
                    color="primary"
                  >
                    Liste drucken
                  </Button>
                </Box>
              </Box>
            </Grid>
            
            {/* Printable area - Task List */}
            <Grid item xs={12}>
              <Paper 
                elevation={isDarkMode ? 0 : 3}
                sx={{ 
                  mt: 2, 
                  p: 0, 
                  bgcolor: isDarkMode ? '#2d3748' : '#ffffff',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <PrintableArea ref={taskListRef} className="printable-content">
                  <PrintHeader>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">Aufgaben Protokoll</Typography>
                      <Typography variant="body2">Erstellt am: {currentDate}</Typography>
                      {selectedUser && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                          <PersonIcon fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            Mitarbeiter: {getUserName(selectedUser)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{companyDetails.name}</Typography>
                      <Typography variant="body2">{companyDetails.address}</Typography>
                      <Typography variant="body2">{companyDetails.contact}</Typography>
                      <Typography variant="body2">{companyDetails.email}</Typography>
                    </Box>
                  </PrintHeader>
                  
                  <Divider sx={{ mt: 1, mb: 3 }} />
                  
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width="8%" sx={{ fontWeight: 'bold' }}>ID</TableCell>
                          <TableCell width="18%" sx={{ fontWeight: 'bold' }}>Aufgabe</TableCell>
                          <TableCell width="20%" sx={{ fontWeight: 'bold' }}>Beschreibung</TableCell>
                          <TableCell width="10%" sx={{ fontWeight: 'bold' }}>ANR</TableCell>
                          <TableCell width="8%" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell width="10%" sx={{ fontWeight: 'bold' }}>Zeiterfassung</TableCell>
                          <TableCell width="10%" sx={{ fontWeight: 'bold' }}>Fälligkeit</TableCell>
                          <TableCell width="16%" sx={{ fontWeight: 'bold' }}>Barcode</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTasks.length > 0 ? (
                          filteredTasks.map((task) => (
                            <TableRow 
                              key={task.id}
                              hover
                              onClick={() => prefillTaskForm(task)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>{task.aufgabeId || task.id.substring(0, 6)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {task.title}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {task.assignedTo ? getUserName(task.assignedTo._id) : getUserName(task.user)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography 
                                  variant="body2" 
                                  className="task-description"
                                  sx={{ 
                                    maxHeight: '60px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                  }}
                                >
                                  {task.description || "Keine Beschreibung"}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {task.anr ? (
                                  <Chip 
                                    label={task.anr} 
                                    size="small" 
                                    icon={<BusinessIcon fontSize="small" />}
                                    variant="outlined"
                                  />
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={getStatusTranslation(task.status)}
                                  size="small"
                                  sx={{
                                    bgcolor: `${getStatusColor(task.status)}20`,
                                    color: getStatusColor(task.status),
                                    border: `1px solid ${getStatusColor(task.status)}`
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <AccessTimeIcon 
                                    fontSize="small" 
                                    sx={{ mr: 0.5, color: '#718096' }}
                                  />
                                  <Typography variant="body2" fontFamily="monospace">
                                    {calculateTotalTime(task)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {task.deadline ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CalendarIcon 
                                      fontSize="small" 
                                      sx={{ mr: 0.5, color: '#718096' }}
                                    />
                                    <Typography variant="body2">
                                      {formatDate(task.deadline)}
                                    </Typography>
                                  </Box>
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', justifyContent: 'center', height: '40px' }}>
                                  <Barcode 
                                    value={task.aufgabeId || task.id.substring(0, 8)} 
                                    width={1.2}
                                    height={38}
                                    fontSize={8}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              <Typography variant="body2" sx={{ py: 3 }}>
                                Keine Aufgaben gefunden, die den Filterkriterien entsprechen.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Summary section */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Zusammenfassung
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, bgcolor: '#f8f9fc' }}>
                          <Typography variant="subtitle2" gutterBottom>Anzahl der Aufgaben</Typography>
                          <Typography variant="h6">{filteredTasks.length}</Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, bgcolor: '#f8f9fc' }}>
                          <Typography variant="subtitle2" gutterBottom>Gesamte Arbeitszeit</Typography>
                          <Typography variant="h6">
                            {formatTimeDisplay(
                              filteredTasks.reduce((total, task) => {
                                if (task.totalTime) {
                                  return total + task.totalTime;
                                }
                                return total;
                              }, 0)
                            )}
                          </Typography>
                        </Paper>
                      </Grid>
                      
                      <Grid item xs={12} sm={4}>
                        <Paper sx={{ p: 2, bgcolor: '#f8f9fc' }}>
                          <Typography variant="subtitle2" gutterBottom>Abgeschlossene Aufgaben</Typography>
                          <Typography variant="h6">
                            {filteredTasks.filter(t => t.status === 'completed' || t.column === 'done').length}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <PrintFooter>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="caption">
                      Dieser Bericht wurde automatisch generiert am {new Date().toLocaleString('de-DE')}
                      <br />
                      © {new Date().getFullYear()} Heidenau CNC - Alle Rechte vorbehalten
                    </Typography>
                  </PrintFooter>
                </PrintableArea>
              </Paper>
            </Grid>
          </Grid>
        )}
        
        {/* Tab 2: Printable Empty Task Form */}
        {tabValue === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fc',
                borderRadius: 2,
                mb: 2
              }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Druckbares Aufgabenformular
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Dies ist ein leeres Formular zum Ausdrucken. Bitte füllen Sie es nach dem Drucken von Hand aus.
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<PrintIcon />}
                  onClick={() => {
                    if (fillableFormRef && fillableFormRef.current) {
                      printFillableForm();
                    } else {
                      toast.error("Druckbereich nicht verfügbar");
                    }
                  }}
                  color="primary"
                  sx={{ mt: 2 }}
                >
                  Formular drucken
                </Button>
              </Box>
              
              <Paper 
                elevation={isDarkMode ? 0 : 3}
                sx={{ 
                  mt: 2, 
                  p: 0, 
                  bgcolor: isDarkMode ? '#2d3748' : '#ffffff',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <PrintableArea ref={fillableFormRef} className="printable-content">
                  <PrintHeader>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">AUFGABENPROTOKOLL</Typography>
                      <Typography variant="body2">Erstellt am: {currentDate}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">{companyDetails.name}</Typography>
                      <Typography variant="body2">{companyDetails.address}</Typography>
                      <Typography variant="body2">{companyDetails.contact}</Typography>
                      <Typography variant="body2">{companyDetails.email}</Typography>
                    </Box>
                  </PrintHeader>
                  
                  <Divider sx={{ mt: 1, mb: 4 }} />
                  
                  {/* Logbook-style header with task info */}
                  <Box className="logbook-header">
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <AssignmentIcon sx={{ fontSize: 28, mr: 1 }} />
                          <Typography variant="h5" fontWeight="bold">AUFGABEN LOGBUCH</Typography>
                        </Box>
                        <Typography variant="body1" fontWeight="bold">
                          {companyDetails.name} - Arbeitszeiterfassung
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ 
                          border: '1px solid #000', 
                          p: 1.5,
                          borderRadius: 1,
                          bgcolor: '#f9f9f9'
                        }}>
                          <Typography variant="body2">
                            <strong>Erstelldatum:</strong> {currentDate}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Dokument-Nr:</strong> {new Date().getTime().toString().substring(8, 13)}
                          </Typography>
                        </Box>
                        {/* Barcode for task */}
                        {taskForm.taskId && (
                          <Box sx={{ 
                            mt: 2, 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1,
                            border: '1px dashed #999',
                            borderRadius: 1
                          }}>
                            <Typography variant="caption" sx={{ mb: 0.5 }}>
                              Aufgaben-ID: {taskForm.taskId}
                            </Typography>
                            <Barcode 
                              value={taskForm.taskId} 
                              width={1.5}
                              height={40}
                              fontSize={10}
                            />
                          </Box>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* Task Identification Section */}
                  <Paper sx={{ p: 2, mb: 3, border: '1px solid #000', borderRadius: 1, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <EventNoteIcon sx={{ mr: 1 }} />
                      AUFGABEN-INFORMATION
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" fontWeight="bold">AUFGABEN-ID:</Typography>
                        <Box className="form-field-box" sx={{ display: 'flex', alignItems: 'center', px: 2, bgcolor: '#fff' }}>
                          <Typography>{taskForm.taskId}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" fontWeight="bold">ERSTELLDATUM:</Typography>
                        <Box className="form-field-box" sx={{ display: 'flex', alignItems: 'center', px: 2, bgcolor: '#fff' }}>
                          <Typography>{taskForm.currentDate}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" fontWeight="bold">ANR-NUMMER:</Typography>
                        <Box className="form-field-box" sx={{ display: 'flex', alignItems: 'center', px: 2, bgcolor: '#fff' }}>
                          <Typography>{taskForm.anr}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight="bold">AUFGABENTITEL:</Typography>
                        <Box className="form-field-box" sx={{ display: 'flex', alignItems: 'center', px: 2, bgcolor: '#fff' }}>
                          <Typography>{taskForm.title}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" fontWeight="bold">BESCHREIBUNG:</Typography>
                        <Box className="form-field-textarea" sx={{ p: 2, overflowY: 'auto', bgcolor: '#fff' }}>
                          <Typography>{taskForm.description}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" fontWeight="bold">ZUSTÄNDIGER MITARBEITER:</Typography>
                        <Box className="form-field-box" sx={{ display: 'flex', alignItems: 'center', px: 2, bgcolor: '#fff' }}>
                          <Typography>{taskForm.assignedTo}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" fontWeight="bold">FÄLLIGKEITSDATUM:</Typography>
                        <Box className="form-field-box" sx={{ display: 'flex', alignItems: 'center', px: 2, bgcolor: '#fff' }}>
                          <Typography>{taskForm.deadline}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                  
                  {/* Time Tracking Section */}
                  <Paper sx={{ p: 2, mb: 3, border: '1px solid #000', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                      <AccessTimeIcon sx={{ mr: 1 }} />
                      ZEITERFASSUNG
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, border: '1px dashed #999', borderRadius: 1, mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>ARBEITSBEGINN:</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2">Datum:</Typography>
                              <Box className="form-field-box" sx={{ height: '40px' }} />
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2">Uhrzeit:</Typography>
                              <Box className="form-field-box" sx={{ height: '40px' }} />
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, border: '1px dashed #999', borderRadius: 1, mb: 2 }}>
                          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>ARBEITSENDE:</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2">Datum:</Typography>
                              <Box className="form-field-box" sx={{ height: '40px' }} />
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2">Uhrzeit:</Typography>
                              <Box className="form-field-box" sx={{ height: '40px' }} />
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight="bold">GESAMTARBEITSZEIT (h:m):</Typography>
                        <Box className="form-field-box" sx={{ height: '40px' }} />
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight="bold">PAUSEN (h:m):</Typography>
                        <Box className="form-field-box" sx={{ height: '40px' }} />
                      </Grid>
                    </Grid>
                  </Paper>
                  
                  {/* Status and Notes Section */}
                  <Paper sx={{ p: 2, mb: 3, border: '1px solid #000', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>STATUS DER AUFGABE:</Typography>
                    <Grid container spacing={2} sx={{ mt: 1, mb: 3 }}>
                      <Grid item xs={4}>
                        <Box className="checkbox-container">
                          <Box className="checkbox" />
                          <Typography>Nicht begonnen</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box className="checkbox-container">
                          <Box className="checkbox" />
                          <Typography>In Bearbeitung</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box className="checkbox-container">
                          <Box className="checkbox" />
                          <Typography>Abgeschlossen</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>FORTSCHRITT (%):</Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      width: '100%', 
                      my: 1,
                      border: '1px solid #000',
                      p: 1,
                      borderRadius: '1px'
                    }}>
                      {[0, 25, 50, 75, 100].map((value) => (
                        <Box 
                          key={value}
                          sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center',
                            width: '20%' 
                          }}
                        >
                          <Box className="checkbox" />
                          <Typography variant="body2">{value}%</Typography>
                        </Box>
                      ))}
                    </Box>
                    
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3 }}>NOTIZEN & BEMERKUNGEN:</Typography>
                    <Box className="form-field-textarea" sx={{ height: '100px' }} />
                  </Paper>
                  
                  {/* Signature Section */}
                  <Paper sx={{ p: 2, mb: 2, border: '1px solid #000', borderRadius: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>BESTÄTIGUNG:</Typography>
                    <Grid container spacing={3} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" fontWeight="bold">UNTERSCHRIFT MITARBEITER:</Typography>
                        <Box className="signature-box" />
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          Datum: ________________
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" fontWeight="bold">UNTERSCHRIFT VORGESETZTER:</Typography>
                        <Box className="signature-box" />
                        <Typography variant="caption" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          Datum: ________________
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                  
                  <PrintFooter>
                    <Divider sx={{ mb: 2, mt: 4 }} />
                    <Typography variant="caption">
                      Dieses Formular wurde generiert am {new Date().toLocaleString('de-DE')}
                      <br />
                      © {new Date().getFullYear()} Heidenau CNC - Alle Rechte vorbehalten
                    </Typography>
                  </PrintFooter>
                </PrintableArea>
              </Paper>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => setTabValue(0)}
                >
                  Zurück zur Liste
                </Button>
                <Button 
                  variant="contained" 
                  startIcon={<PrintIcon />}
                  onClick={() => {
                    if (fillableFormRef && fillableFormRef.current) {
                      printFillableForm();
                    } else {
                      toast.error("Druckbereich nicht verfügbar");
                    }
                  }}
                  color="primary"
                >
                  Formular drucken
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      
      {/* Tab 3: Task Overview List */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ 
              p: 2, 
              bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fc',
              borderRadius: 2,
              mb: 2
            }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Aufgaben Übersicht zum Ausdrucken
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Diese Ansicht zeigt alle Aufgaben in einem kompakten, druckerfreundlichen Format.
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<PrintIcon />}
                onClick={() => {
                  if (taskOverviewRef && taskOverviewRef.current) {
                    printTaskOverview();
                  } else {
                    toast.error("Druckbereich nicht verfügbar");
                  }
                }}
                color="primary"
                sx={{ mt: 2 }}
              >
                Übersicht drucken
              </Button>
            </Box>
            
            <Paper 
              elevation={isDarkMode ? 0 : 3}
              sx={{ 
                mt: 2, 
                p: 0, 
                bgcolor: isDarkMode ? '#2d3748' : '#ffffff',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : 'none',
              }}
            >
              <PrintableArea ref={taskOverviewRef} className="printable-content">
                <Box sx={{ p: 3, width: '100%' }}>
                  {/* Company Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid #000'
                  }}>
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        Aufgabenliste
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {companyDetails.name}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h6" fontWeight="bold">
                        Datum: {new Date().toLocaleDateString('de-DE')}
                      </Typography>
                      <Typography variant="body1">
                        Insgesamt: {filteredTasks.length} Aufgaben
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Tasks Table */}
                  <TableContainer sx={{ mb: 3 }}>
                    <Table size="small" sx={{ 
                      borderCollapse: 'collapse', 
                      '& th, & td': { 
                        border: '1px solid #000',
                        padding: '8px 12px' 
                      },
                      '& th': {
                        backgroundColor: '#f0f0f0',
                        fontWeight: 'bold'
                      }
                    }}>
                      <TableHead>
                        <TableRow>
                          <TableCell width="5%">Nr.</TableCell>
                          <TableCell width="8%">ID</TableCell>
                          <TableCell width="20%">Aufgabe</TableCell>
                          <TableCell width="25%">Beschreibung</TableCell>
                          <TableCell width="12%">ANR</TableCell>
                          <TableCell width="8%">Status</TableCell>
                          <TableCell width="9%">Fälligkeit</TableCell>
                          <TableCell width="13%">Barcode</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTasks.map((task, index) => (
                          <TableRow key={task.id}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{task.aufgabeId || task.id.substring(0, 6)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {task.title}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#666' }}>
                                {task.assignedTo ? getUserName(task.assignedTo._id) : getUserName(task.user)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                sx={{ whiteSpace: 'pre-wrap' }}
                              >
                                {task.description ? 
                                  (task.description.length > 150 ? 
                                    task.description.substring(0, 150) + '...' : 
                                    task.description) : 
                                  "Keine Beschreibung"}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {task.anr || "-"}
                            </TableCell>
                            <TableCell align="center" sx={{ 
                              fontWeight: 'medium',
                              color: getStatusColor(task.status)
                            }}>
                              {getStatusTranslation(task.status)}
                            </TableCell>
                            <TableCell align="center">
                              {task.deadline ? formatDate(task.deadline) : "-"}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', justifyContent: 'center', height: '50px' }}>
                                <Barcode 
                                  value={task.aufgabeId || task.id.substring(0, 8)} 
                                  width={1.2}
                                  height={40}
                                  fontSize={8}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Notes Section */}
                  <Box sx={{ border: '1px solid #000', p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Notizen:
                    </Typography>
                    <Box sx={{ height: '120px', borderBottom: '1px dashed #999' }}></Box>
                    <Box sx={{ height: '120px', borderBottom: '1px dashed #999', mt: 2 }}></Box>
                    <Box sx={{ height: '120px', mt: 2 }}></Box>
                  </Box>
                  
                  {/* Footer */}
                  <Box sx={{ 
                    mt: 4, 
                    pt: 1, 
                    borderTop: '1px solid #999',
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    color: '#666'
                  }}>
                    <Typography variant="body2">
                      Diese Liste wurde erstellt am {new Date().toLocaleString('de-DE')}
                    </Typography>
                    <Typography variant="body2">
                      © {new Date().getFullYear()} {companyDetails.name} - Alle Rechte vorbehalten
                    </Typography>
                  </Box>
                </Box>
              </PrintableArea>
            </Paper>
          </Grid>
        </Grid>
      )}

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit" startIcon={<CloseIcon />}>
          Schließen
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => {
            try {
              if (tabValue === 0) {
                if (taskListRef && taskListRef.current) {
                  printTaskList();
                } else {
                  toast.error("Druckbereich nicht verfügbar");
                }
              } else if (tabValue === 1) {
                if (fillableFormRef && fillableFormRef.current) {
                  printFillableForm();
                } else {
                  toast.error("Druckbereich nicht verfügbar");
                }
              } else if (tabValue === 2) {
                if (taskOverviewRef && taskOverviewRef.current) {
                  printTaskOverview();
                } else {
                  toast.error("Druckbereich nicht verfügbar");
                }
              }
            } catch (error) {
              console.error("Print error:", error);
              toast.error("Druckfehler: " + error.message);
            }
          }}
          startIcon={<PrintIcon />}
        >
          {tabValue === 0 ? 'Liste drucken' : tabValue === 1 ? 'Formular drucken' : 'Übersicht drucken'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskPrintModal;